import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabase";
import LottieLoader from "../components/LottieLoader";

const PracticeInterviewQuestionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isTipsExpanded, setIsTipsExpanded] = useState(false);
  const [isScrollingPaused, setIsScrollingPaused] = useState(false);
  const bannerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const isScrollingPausedRef = useRef(false);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user?.id) {
        navigate("/login");
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_tokens')
          .select('mock_interview_paid, mock_interview_consent')
          .eq('user_id', user.id)
          .single();

        if (!error && data) {
          const paid = data.mock_interview_paid || false;
          const consented = data.mock_interview_consent || false;
          
          // User must have both paid AND consented to access
          if (paid && consented) {
            setHasAccess(true);
          } else {
            // Redirect to employee dashboard - clicking sidebar link will show the modal
            navigate("/employee/dashboard");
          }
        } else {
          // No record found, redirect to employee dashboard
          navigate("/employee/dashboard");
        }
      } catch (err) {
        console.error("Error checking mock interview access:", err);
        navigate("/employee/dashboard");
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, navigate]);

  // Update ref when state changes
  useEffect(() => {
    isScrollingPausedRef.current = isScrollingPaused;
  }, [isScrollingPaused]);

  // Auto-scroll banner on mobile
  useEffect(() => {
    if (!hasAccess) return; // Don't start scrolling until page is ready

    const banner = bannerRef.current;
    if (!banner) return;

    const isMobile = window.innerWidth < 768; // md breakpoint
    if (!isMobile) {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    // If paused, clear interval and return
    if (isScrollingPaused) {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    // Check if content overflows
    const checkOverflow = () => {
      return banner.scrollWidth > banner.clientWidth;
    };

    if (!checkOverflow()) {
      return; // No need to scroll if content fits
    }

    // Clear any existing interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    // Always start from the current banner scroll position when resuming
    scrollPositionRef.current = banner.scrollLeft;

    const scrollSpeed = 1; // pixels per frame
    const scrollDelay = 30; // milliseconds

    const scroll = () => {
      const banner = bannerRef.current;
      if (!banner || isScrollingPausedRef.current) {
        return;
      }

      scrollPositionRef.current += scrollSpeed;
      const maxScroll = banner.scrollWidth - banner.clientWidth;

      if (scrollPositionRef.current >= maxScroll) {
        // Reset to start smoothly
        scrollPositionRef.current = 0;
        banner.scrollLeft = 0;
      } else {
        banner.scrollLeft = scrollPositionRef.current;
      }
    };

    scrollIntervalRef.current = setInterval(scroll, scrollDelay);

    // Cleanup on resize
    const handleResize = () => {
      const banner = bannerRef.current;
      if (!banner) return;

      if (window.innerWidth >= 768) {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
        banner.scrollLeft = 0;
      } else {
        // Restart scrolling on mobile if it was paused
        if (!isScrollingPausedRef.current && banner.scrollWidth > banner.clientWidth) {
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
          }
          let scrollPosition = 0;
          const scrollSpeed = 1;
          const scrollDelay = 30;
          const scroll = () => {
            if (!banner || isScrollingPausedRef.current) return;
            scrollPosition += scrollSpeed;
            const maxScroll = banner.scrollWidth - banner.clientWidth;
            if (scrollPosition >= maxScroll) {
              scrollPosition = 0;
              banner.scrollLeft = 0;
            } else {
              banner.scrollLeft = scrollPosition;
            }
          };
          scrollIntervalRef.current = setInterval(scroll, scrollDelay);
        }
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [hasAccess, isScrollingPaused]);

  const handleBannerClick = () => {
    // Only toggle on mobile
    if (window.innerWidth < 768) {
      setIsScrollingPaused(prev => !prev);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return null; // Will redirect, so return nothing
  }

  const topics = [
    { id: 1, name: "General Motivation and Career Goals" },
    { id: 2, name: "Clinical Knowledge and Patient Management" },
    { id: 3, name: "Clinical Governance, Systems and Public Health" },
    { id: 4, name: "Ethics and Professionalism" },
    { id: 5, name: "Human Resources and Team Dynamics" },
    { id: 6, name: "Personal Qualities and Resilience" },
    { id: 7, name: "Research and Academic Development" },
  ];

  const specialties = [
    { id: 8, name: "General Practice" },
    { id: 9, name: "Emergency Medicine" },
    { id: 10, name: "Internal Medicine" },
    { id: 11, name: "Paediatrics" },
    { id: 12, name: "General Surgery" },
    { id: 13, name: "Obstetrics & Gynaecology" },
    { id: 14, name: "Anaesthesia" },
    { id: 15, name: "Psychiatry" },
    { id: 16, name: "ECGs" },
  ];

  return (
    <div className="w-full h-screen flex flex-col relative" style={{ marginLeft: '-1.5rem', marginRight: '-1.5rem', width: 'calc(100% + 3rem)' }}>
      {/* Custom style for tablet media query */}
      <style>{`
        @media (min-width: 768px) and (max-width: 1500px) {
          .tips-section-tablet {
            left: 17rem !important;
          }
        }
        @media (min-width: 1500px) {
          .tips-section-desktop {
            left: auto !important;
            right: 1020px !important;
          }
        }
      `}</style>
      {/* Tips Section - Narrow collapsed, floating card when expanded */}
      <div 
        className="fixed top-4 left-16 tips-section-tablet tips-section-desktop z-[9999]"
      >
        {/* Collapsed Tips Button - Narrow */}
        <div 
          className="inline-flex items-center gap-2 px-0 py-2 rounded cursor-pointer transition-colors"
          onClick={() => setIsTipsExpanded(!isTipsExpanded)}
        >
          <img 
            src="/bulb.svg" 
            alt="Tips" 
            className="w-5 h-5 flex-shrink-0"
          />
          <span className="font-semibold text-gray-900 whitespace-nowrap">Question Categories List</span>
        </div>

        {/* Expanded Tips Content - Floating Card */}
        {isTipsExpanded && (
          <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-lg z-[9999]" style={{ width: 'max-content', maxWidth: '360px' }}>
            <div className="p-3">
              {/* Header with X icon */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <img 
                    src="/bulb.svg" 
                    alt="Bulb" 
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <h3 className="text-gray-900 text-sm font-semibold">New Question Categories</h3>
                </div>
                <button
                  onClick={() => setIsTipsExpanded(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-700 text-xs mb-2">
                Start a New Question by picking a topic
              </p>
              
              {/* All topics stacked vertically */}
              <div className="space-y-0.5">
                {[...topics, ...specialties].map((item) => (
                  <div
                    key={item.id}
                    className="text-gray-700 text-xs py-0"
                  >
                    {item.id}. {item.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Black Information Banner */}
      <div className="bg-black text-white -mt-7">
        <div 
          ref={bannerRef}
          onClick={handleBannerClick}
          className="px-6 py-4 flex items-center gap-4 whitespace-nowrap overflow-x-auto md:overflow-x-visible md:justify-center cursor-pointer md:cursor-default"
          style={{ 
            scrollBehavior: 'auto',
            WebkitOverflowScrolling: 'touch',
            overflowX: 'auto'
          }}
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[13px]">Missing Dialogue:</span>
            <span className="text-[13px]">If your input doesn't appear, try refreshing the page.</span>
          </div>
          <div className="w-px h-4 bg-white flex-shrink-0"></div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[13px]">Wait Time:</span>
            <span className="text-[13px]">Please be patient, this feature is still in beta.</span>
          </div>
          <div className="w-px h-4 bg-white flex-shrink-0"></div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[13px]">Start a New Question:</span>
            <span className="text-[13px]">pick from category list... </span>
           
          </div>
        </div>
      </div>
      
      {/* Iframe */}
      <div className="flex-1 w-full">
        <iframe
          src="https://app.relevanceai.com/agents/d7b62b/333d46e1-f497-47d8-9781-217d87713b78/b3f8e38f-e277-42e1-8172-df0d5c8d53ce/embed-chat?hide_tool_steps=false&hide_file_uploads=true&hide_conversation_list=true&bubble_style=agent&primary_color=%23000000&bubble_icon=pd%2Fchat&input_placeholder_text=Type+here...+select+a+question+category+%281-16%29+for+a+new+question&hide_logo=true&hide_description=true"
          width="100%"
          height="100%"
          frameBorder="0"
          allow="microphone"
          title="Practice Interview Questions"
        />
      </div>
    </div>
  );
};

export default PracticeInterviewQuestionsPage;

